/**
 * Utility functions for photo handling and processing
 */

export interface PhotoUploadResult {
  url: string;
  thumbnailUrl?: string;
  width: number;
  height: number;
  size: number;
}

/**
 * Compress and resize image file
 */
export const compressImage = (
  file: File, 
  maxWidth: number = 1920, 
  maxHeight: number = 1080, 
  quality: number = 0.8
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        file.type,
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Generate thumbnail from image file
 */
export const generateThumbnail = (
  file: File, 
  size: number = 300
): Promise<File> => {
  return compressImage(file, size, size, 0.7);
};

/**
 * Get image dimensions
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Validate image file
 */
export const validateImageFile = (
  file: File, 
  maxSize: number = 10 * 1024 * 1024, // 10MB
  allowedTypes: string[] = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
): { isValid: boolean; error?: string } => {
  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `Tipo de archivo no soportado. Formatos permitidos: ${allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')}`
    };
  }

  // Check file size
  if (file.size > maxSize) {
    const maxSizeMB = maxSize / (1024 * 1024);
    return {
      isValid: false,
      error: `El archivo es demasiado grande. Tamaño máximo: ${maxSizeMB}MB`
    };
  }

  return { isValid: true };
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Extract EXIF data from image (basic implementation)
 */
export const extractImageMetadata = async (file: File): Promise<{
  dateTime?: string;
  location?: { lat: number; lng: number };
  camera?: string;
  settings?: string;
}> => {
  // This is a simplified implementation
  // In a real app, you might use a library like exif-js or piexifjs
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Basic metadata extraction
      resolve({
        dateTime: new Date(file.lastModified).toISOString(),
        // location: undefined, // Would require EXIF parsing
        // camera: undefined,   // Would require EXIF parsing
        // settings: undefined  // Would require EXIF parsing
      });
    };
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Create photo post data for database
 */
export const createPhotoPostData = (
  userId: string,
  content: string,
  photoUrls: string[],
  metadata: {
    location?: string;
    tags: string[];
    category: string;
    visibility: string;
  }
) => {
  return {
    user_id: userId,
    type: 'image' as const,
    content,
    media_urls: photoUrls,
    visibility: metadata.visibility,
    metadata: {
      location: metadata.location,
      tags: metadata.tags,
      category: metadata.category,
      photo_count: photoUrls.length
    }
  };
};

/**
 * Upload photos to Supabase Storage
 */
export const uploadPhotosToStorage = async (
  files: File[],
  userId: string,
  bucket: string = 'photos'
): Promise<string[]> => {
  // This would be implemented with actual Supabase Storage calls
  // For now, return mock URLs
  return files.map((file, index) => 
    `https://example.com/storage/${bucket}/${userId}/${Date.now()}-${index}-${file.name}`
  );
};